import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowLeft } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const CategoryPage = () => {
  const { categorySlug } = useParams();
  // The article library is a build-time snapshot (src/data/articlesIndex.json)
  // loaded as its own webpack chunk. The import resolves same-origin during
  // the react-snap prerender, so the full article list for this category is
  // baked into the static HTML and every article gets a crawlable internal
  // link.
  const [index, setIndex] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    import('../data/articlesIndex.json')
      .then((mod) => {
        if (!cancelled) setIndex(mod.default || mod);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (!index && !loadFailed) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sortio-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const category = index
    ? index.categories.find((c) => c.slug === categorySlug)
    : null;

  if (loadFailed || !category) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-16">
        <Helmet>
          <title>Category Not Found | Sortio</title>
          {/* Only noindex when the index loaded and this category genuinely is
              not in it. If the index chunk itself failed (a stale chunk hash
              after a deploy, say) the category may well still exist, and
              deindexing a live hub page over a transient failure is worse than
              leaving it alone. */}
          {!loadFailed && <meta name="robots" content="noindex" />}
        </Helmet>
        <div className="container mx-auto px-6">
          <div className="apple-panel p-10 max-w-2xl mx-auto text-center">
            <h1 className="text-3xl font-bold mb-4 text-apple-50">Category not found</h1>
            <p className="text-apple-300 mb-8">
              This category does not exist. Browse the full article library instead.
            </p>
            <Link to="/articles" className="apple-button inline-flex items-center justify-center px-5 py-2.5">
              <ArrowLeft className="mr-2 w-4 h-4" />
              All Articles
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const articles = index.articles.filter((a) => a.category_slug === categorySlug);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Background blur orbs */}
      <div className="fixed -bottom-32 -right-32 w-96 h-96 bg-sortio-primary/10 rounded-full blur-3xl"></div>
      <div className="fixed -top-32 -left-32 w-96 h-96 bg-sortio-secondary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        <Helmet>
          <title>{`${category.name} Articles | Sortio`}</title>
          <meta
            name="description"
            content={`Articles in the ${category.name} category from Sortio, the AI file sorter. Practical guides and tips for organizing files with AI.`}
          />
          <link rel="canonical" href={`https://www.getsortio.com/articles/${categorySlug}`} />
        </Helmet>

        <Breadcrumbs category={category.name} articleTitle={null} />

        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 text-apple-50">
            {category.name}
          </h1>
          <p className="text-apple-300">
            {articles.length} {articles.length === 1 ? 'article' : 'articles'} in this category, newest first.
          </p>
        </div>

        <ul className="space-y-3">
          {articles.map((article) => (
            <li key={article.slug} className="apple-panel p-5">
              <Link
                to={`/articles/${categorySlug}/${article.slug}`}
                className="text-lg font-medium text-apple-100 hover:text-sortio-primary transition-colors"
              >
                {article.title}
              </Link>
              <span className="flex items-center text-sm text-apple-400 mt-1.5">
                <Calendar className="w-4 h-4 mr-1.5" />
                {formatDate(article.created_at)}
              </span>
            </li>
          ))}
        </ul>

        {articles.length === 0 && (
          <div className="apple-panel p-8 text-center">
            <p className="text-apple-300">No articles found in this category.</p>
          </div>
        )}

        <div className="mt-10">
          <Link
            to="/articles"
            className="apple-panel-button inline-flex items-center px-5 py-2.5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            All Articles
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
